import Banner from '@cpg-its/ads.containers.banner';
import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';
import { Redirect } from 'react-router-dom';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { YesNoOptions } from '../../../../../../global/constants/lists';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { useBasePath } from '../../../../../../global/hooks/useBasePath';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { ArcRoutes } from '../../../../constants/routes';
import { LayDcPlanAdoptionContentModel, LayDcPlanFormModel, QccoStatus } from '../models/plan-adoption-form-models';
import { QccoPlanAdoptionWizard } from '../wizards/qcco-plan-adoption-wizard';

interface Props {
    wizard: QccoPlanAdoptionWizard;
}

function QccoQuestionsForm(props: Props) {

    const { wizard } = props;
    const content: LayDcPlanAdoptionContentModel = wizard?.getFormContentModel();
    const gpsItem = useGpsType(content?.gpsType);
    const apiErrors = useApiResponseError();
    const currentInstitution = wizard?.wizardOptions?.currentInstitution;
    const initialValues: LayDcPlanFormModel = wizard?.getFormInitialValue();
    const validationSchema = wizard?.getFormValidationSchema();

    const institutionRootPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}`);
    const isIndemnificationComplete = wizard?.isIndemnificationComplete();
    const isIndemnificationRequired = wizard?.isIndemnificationRequired();

    function renderQualificationError(values: LayDcPlanFormModel) {

        const qccoCalculatedStatus = wizard?.calculateQccoStatus(values);

        if (qccoCalculatedStatus !== QccoStatus.Error) return null;

        return (<ContentSpacer>
            <Banner
                banner={{
                    type: 'error',
                    label: <p><strong>{content?.qccoQualificationErrorStatusTitle}</strong></p>,
                    description: <WysiwygWithReactLink content={content?.qccoQualificationErrorStatusText} />,
                }}
            />
        </ContentSpacer>);
    }

    function renderQualificationStatus(values: LayDcPlanFormModel) {

        const qccoCalculatedStatus = wizard?.calculateQccoStatus(values);

        if (!qccoCalculatedStatus) return null;

        if (qccoCalculatedStatus === QccoStatus.Qualified) {
            return (
                <>
                    <h4>{content?.qccoQualificationYesStatusTitle}</h4>
                    <WysiwygWithReactLink content={content?.qccoQualificationYesStatusText} />
                </>
            );
        }

        if (qccoCalculatedStatus === QccoStatus.NotQualified) {
            return (
                <>
                    <h4>{content?.qccoQualificationNoStatusTitle}</h4>
                    <WysiwygWithReactLink content={content?.qccoQualificationNoStatusText} />
                </>
            );
        }

        return null;
    }

    function renderIdenmificationBanner() : any {
        if (!isIndemnificationRequired) return null;

        const indemnifySuccess = isIndemnificationComplete && initialValues?.complyEpiscopalChurchLayPensionSystem !== YesNoIndicators.No;

        return <ContentSpacer>
            <Banner
                banner={{
                    type: 'success',
                    label: <WysiwygWithReactLink content={indemnifySuccess ? content?.indemnifySuccessMessage : content?.indemnifySkippedMessage} />,
                }}
            />
        </ContentSpacer>;
    }

    if (!wizard) {
        return <Redirect to={institutionRootPath} />;
    }

    if (!isIndemnificationComplete) {
        return <Redirect to={wizard?.wizardOptions?.featureRootPath} />;
    }

    return (
        <PageContainer>
            <MainContent>
                <Formik
                    initialValues={initialValues}
                    onSubmit={
                        (values) => wizard?.onSubmit(
                            {
                                values,
                                clientId: currentInstitution?.org_client_nbr,
                            })
                    }
                    enableReinitialize
                    initialErrors={apiErrors}
                    validationSchema={validationSchema}
                >
                    {(formProps: FormikProps<LayDcPlanFormModel>) => (
                        <>
                            <Form>
                                <Grid>
                                    <FormWrapper
                                        formikProps={formProps}
                                        onCancel={() => wizard?.onCancel({
                                            values: formProps.values,
                                            clientId: currentInstitution?.org_client_nbr,
                                        })}
                                        submitDisabled={wizard?.isSubmitDisabled(formProps.values)}
                                        preventButtonDisableValidation={wizard?.preventButtonDisableValidation()}
                                        submitText={content?.submitLabel}
                                        cancelText={content?.cancelLabel}
                                    >
                                        {renderIdenmificationBanner()}
                                        <PageHeader
                                            headerText={content?.pageTitle}
                                            description={content?.PageDescription}
                                        />
                                        <Hidden mdUp>
                                            <SideBarContent>
                                                <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                            </SideBarContent>
                                        </Hidden>
                                        <DividerSection removeDivider >
                                            <FormGroup>
                                                <FormField
                                                    name='qccoQuestion1'
                                                    label={content?.qccoQuestion1Label}
                                                    component="radioGroup"
                                                    options={YesNoOptions}
                                                    touched={formProps.touched}
                                                    errors={formProps.errors}
                                                    required
                                                />

                                                <FormField
                                                    name='qccoQuestion2'
                                                    label={content?.qccoQuestion2Label}
                                                    component="radioGroup"
                                                    options={YesNoOptions}
                                                    touched={formProps.touched}
                                                    errors={formProps.errors}
                                                    required
                                                />

                                                <FormField
                                                    name='qccoQuestion3'
                                                    label={content?.qccoQuestion3Label}
                                                    component="radioGroup"
                                                    options={YesNoOptions}
                                                    touched={formProps.touched}
                                                    errors={formProps.errors}
                                                    required
                                                />
                                            </FormGroup>
                                            <ContentSpacer>
                                                {renderQualificationError(formProps.values)}
                                            </ContentSpacer>
                                            <ContentSpacer>
                                                <ContentSpacer>
                                                    <h3>{content?.qccoQualificationStatusTitle}</h3>
                                                </ContentSpacer>
                                                {renderQualificationStatus(formProps.values)}
                                                <WysiwygWithReactLink content={content?.qccoQualificationStatusText} />
                                            </ContentSpacer>
                                        </DividerSection>
                                    </FormWrapper>
                                </Grid>
                            </Form>
                        </>
                    )}
                </Formik>
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );

}

export default QccoQuestionsForm;